"use client";

import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";

interface Heading {
  id: string;
  text: string;
  level: number;
}

export function TOC({ headings }: { headings: Heading[] }) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [topOffset, setTopOffset] = useState(0);
  const linkRefs = useRef<Record<string, HTMLAnchorElement | null>>({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visible.length > 0) {
          const id = visible[0].target.id;
          setActiveId(id);

          const el = linkRefs.current[id];
          if (el) {
            const elTop = el.offsetTop;
            setTopOffset(elTop + el.offsetHeight / 2 - 6); // Center triangle
          }
        }
      },
      { rootMargin: "0px 0px -70% 0px", threshold: [0.1, 0.5] }
    );

    const elements = document.querySelectorAll("h1, h2, h3");
    elements.forEach((el) => observer.observe(el));
    return () => elements.forEach((el) => observer.unobserve(el));
  }, []);

  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const [headingHeight, setHeadingHeight] = useState(0);

  useEffect(() => {
    if (headingRef.current) {
      setHeadingHeight(headingRef.current.offsetHeight + 10); // add margin spacing
    }
  }, []);

  return (
    <div
      className={cn(
        "hidden lg:block sticky top-[55px] toc-top pr-20 transition-all duration-300",
        {
          "opacity-0": activeId === null,
          "opacity-100": activeId !== null,
        }
      )}
    >
      <div className="relative">
        <div ref={headingRef} className="text-xl font-semibold mb-2">
          Editorial Overview
        </div>

        {/* Arrow */}
        {activeId && (
          <div
            className="absolute -left-[0px] w-0 h-0 border-y-[6px] border-l-[8px] border-y-transparent border-l-neutral-900 dark:border-l-neutral-50 transition-all duration-300"
            style={{ top: topOffset }}
          />
        )}

        <nav className="border-l-[1px] border-l-neutral-900 dark:border-l-neutral-50 pl-4 text-sm text-muted-foreground space-y-2 max-h-[calc(100vh-8rem)] overflow-auto">
          {headings.map(({ id, text, level }) => {
            const isActive = id === activeId;
            return (
              <a
                key={id}
                href={`#${id}`}
                ref={(el) => {
                  linkRefs.current[id] = el;
                }}
                className={cn(
                  "block transition-colors break-words line-clamp-1",
                  level === 2 ? "ml-0 font-medium" : "ml-4",
                  isActive
                    ? "text-neutral-950 font-bold dark:text-neutral-50"
                    : "hover:text-foreground text-neutral-800 dark:text-neutral-400 dark:hover:text-neutral-50"
                )}
              >
                {text}
              </a>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
